<script setup lang="ts">

import AuthenticatedLayout from "@/layout/AuthenticatedLayout.vue";
import {useGlobalStore} from "@/store";
import {onMounted, ref, watch, computed, defineAsyncComponent} from "vue";
import {Institution, Tenant} from "@YenzaCT/sdk";
import yapi from "@/lib/yapi";
import {useReportingStore} from "@/store/reporting";
import PageHeader from "@/components/PageHeader.vue";

import {useRouter, useRoute} from "vue-router";
import {useApiCall} from "@/composables/useApiCall";
import {useRoutedTabbedDashboard} from "@/composables/useRoutedTabbedDashboard";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

// Lazy load tab components
const DashboardTab = defineAsyncComponent(() => import("./tabs/DashboardTab.vue"));
const InstitutionsTab = defineAsyncComponent(() => import("./tabs/InstitutionsTab.vue"));
const StudentsTab = defineAsyncComponent(() => import("./tabs/StudentsTab.vue"));
const LicensesTab = defineAsyncComponent(() => import("./tabs/LicensesTab.vue"));
const ChoicesTab = defineAsyncComponent(() => import("../ui/ChoicesTab.vue"));
const AssessmentsTab = defineAsyncComponent(() => import("../ui/AssessmentsTab.vue"));

const reportingStore = useReportingStore();

const store = useGlobalStore();
const router = useRouter();
const route = useRoute();
const {executeApi} = useApiCall();
const initialized = ref(false);

const user = store.user;
const institutions = ref<Institution[]>([]);
const tenantId = computed(() => route.params.tenantId as string);
const {activeTab} = useRoutedTabbedDashboard("schoolsDashboardWithTenant", tenantId.value);
const selectedTenant = ref<string>(route.params.tenantId as string);

const tenant = ref<Tenant>();

// Map tab names to components
const tabComponents = {
  dashboard: DashboardTab,
  institutions: InstitutionsTab,
  students: StudentsTab,
  licenses: LicensesTab,
  choices: ChoicesTab,
  assessments: AssessmentsTab
};

// Get the current tab component
const currentTabComponent = computed(() =>
  tabComponents[activeTab.value as keyof typeof tabComponents] || DashboardTab);

onMounted(async () => {
  initialized.value = false;
  await setup();
  initialized.value = true;
});

async function setup() {
  await fetchTenant();
  await fetchInstitutions();

  reportingStore.setFiltersAndRefresh({
    tenants: [tenantId.value],
  });
}

watch(() => route.params.tenantId, async () => {
  await setup();
});

watch(() => selectedTenant.value, async (newTenant: string | undefined) => {
  await router.replace({
    name: "schoolsDashboardWithTenant",
    params: {
      tenantId: newTenant,
      tab: activeTab.value
    }
  });
});
function handleViewAllUsersClick() {
  router.push({
    name: "users",
    query: {
      filterTenant: tenant.value?._id,
    },
  });
}

async function fetchInstitutions() {
  const result = await executeApi(async () => {
    const res = await yapi.admin.institution.paginate({
      filterTenant: tenantId.value,
    });
    return res.data.docs;
  });
  if (result) {
    institutions.value = result;
  }
}

async function fetchTenant() {
  const result = await executeApi(async () => {
    const response = await yapi.admin.tenant.get(tenantId.value as string);
    return response.data;
  });

  if (result) {
    tenant.value = result;
    selectedTenant.value = tenant.value._id;
  }
}

</script>

<template>
  <authenticated-layout class="" :breadcrumbs="[
    { text: 'My Schools', to: { name: 'schoolsDashboard' } },
    { text: tenant?.title || '' }
  ]">

    <page-header :title="tenant?.title"
                 :description="`Welcome back, ${user?.profile?.firstName}. Here is what is happening across your institutions`">
      <template #actions>
        <tenant-search-autocomplete
          v-model="selectedTenant"
          :clearable="false"
          entity-type="tenant"
        />
      </template>
    </page-header>

    <v-divider/>

    <v-container class="mt-0 mx-auto pa-0 border-b" fluid>
      <v-tabs v-model="activeTab" grow>
        <v-tab value="dashboard">
          Dashboard
        </v-tab>
        <v-tab value="institutions">
          Institutions
        </v-tab>
        <v-tab value="students">
          Students
        </v-tab>
        <v-tab value="licenses">
          Licenses
        </v-tab>
        <v-tab value="choices">
          Choices
        </v-tab>
        <v-tab value="assessments">
          Assessments
        </v-tab>
      </v-tabs>
    </v-container>

    <div v-if="tenant">
      <v-container class="mx-auto" fluid>
        <component
          v-if="initialized"
          :is="currentTabComponent"
          :tenant="tenant"
          :institutions="institutions"
          @view-all-users="handleViewAllUsersClick"
        />
      </v-container>
    </div>
  </authenticated-layout>
</template>

<style scoped>

</style>
